import { SyncStatus } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useOpenBankingAPI } from '../../../../../api/use-open-banking-api';
import { useUserInfo } from '../../../../../contexts/user-context/use-user-info';
import { ErrorSection } from '../../../../../design-system/error-section/error-section';
import { LoadingSection } from '../../../../../design-system/loading/loading';
import { CompanyView, InstitutionView } from '../../../../../types/entity-types';
import { AccountConnectedErrorModal } from './account-connected-error-modal';
import { AccountConnectedSuccessModal } from './account-connected-success-modal';
import { ConnectAccountDialog } from './connect-account-dialog';
import { InstitutionList } from './institution-list';
import { PsuIdCollectionModal } from './psu-id-collection-modal';

type InstitutionBasicInfo = {
  name: string;
  logo: string;
};

type InstitutionListWrapperProps = {
  searchQuery: string;
  company: CompanyView;
};

// Institutions that require PSU-ID (Payment Service User Identifier)
// Deutsche Bank and Noris Bank require PSU-ID in format: branch_number (3 digits) + account_number (7 digits)
// See https://docs.yapily.com/pages/open-banking-flow/user-authorisation/psu_identifiers/
const PSU_ID_REQUIRED_INSTITUTIONS = ['deutschebank-pfb_de', 'deutschebank-noris_de'];

export const InstitutionListWrapper = ({ company, searchQuery }: InstitutionListWrapperProps) => {
  const navigate = useNavigate();
  const { authenticatedInfo } = useUserInfo();
  const companyCountry = company.legalEntity.country;
  const { data, isLoadingData, errorData, mutations } = useOpenBankingAPI(company);

  const [selectedInstitution, setSelectedInstitution] = useState<InstitutionView | null>(null);
  const [isPsuIdModalOpen, setIsPsuIdModalOpen] = useState(false);
  const [collectedPsuId, setCollectedPsuId] = useState<string | undefined>(undefined);
  const [isAccountConnectModalOpen, setIsAccountConnectModalOpen] = useState(false);
  const [isAccountConnectErrorModalOpen, setIsAccountConnectErrorModalOpen] = useState(false);
  const connectionCompletedRef = useRef(false);

  useEffect(() => {
    if (!connectionCompletedRef.current) {
      if (data.syncStatusData?.syncStatus === SyncStatus.IN_PROGRESS) {
        toast.info('Syncing of accounts in progress');

        return;
      }

      if (data.syncStatusData?.syncStatus === SyncStatus.COMPLETE) {
        setIsAccountConnectModalOpen(true);

        connectionCompletedRef.current = true;

        return;
      }

      if (data.syncStatusData?.syncStatus === SyncStatus.FAILED) {
        Sentry.captureMessage('Account sync status failed', {
          extra: {
            data: data.syncStatusData,
            message: 'Account sync status failed',
          },
        });

        // TODO: I am not sure if the success modal should be displayed in this case scenario
        // We seem to get sync account and transactions numbers. See TENG-1332.
        toast.error('There was a problem syncing your account');

        return;
      }
    }

    return () => {
      connectionCompletedRef.current = false;
    };
  }, [data.syncStatusData]);

  useEffect(() => {
    if (errorData.isConnectedError) {
      setIsAccountConnectErrorModalOpen(true);
    }
  }, [errorData.isConnectedError]);

  // Log auth link errors to Sentry
  useEffect(() => {
    if (errorData.authLinkError) {
      Sentry.captureException(errorData.authLinkError, {
        extra: {
          institutionName: selectedInstitution?.name,
          institutionId: selectedInstitution?.externalId,
          companyId: company.id,
        },
      });
    }
  }, [errorData.authLinkError, selectedInstitution, company.id]);

  if (isLoadingData.isLoadingInstitutions || isLoadingData.isLoadingSyncStatus) {
    return (
      <div className="col-span-full flex items-center justify-center py-8">
        <LoadingSection text="Loading..." />
      </div>
    );
  }

  if (!data.institutionsData) {
    Sentry.addBreadcrumb(errorData.institutionsError, {
      extra: {
        companyId: company.id,
        message: 'There was an error loading the list of institutions',
      },
    });

    toast.error('There was an error loading institutions');

    return <ErrorSection />;
  }

  const onInstitutionSelect = async (institution: InstitutionView) => {
    setSelectedInstitution(institution);
    const institutionDisplayData = getInstitutionDisplayData(institution);

    localStorage.setItem('selectedInstitution', JSON.stringify(institutionDisplayData));

    // Check if this institution requires PSU-ID
    const requiresPsuId = PSU_ID_REQUIRED_INSTITUTIONS.includes(institution.externalId);

    if (requiresPsuId) {
      // Show PSU-ID collection modal first
      setIsPsuIdModalOpen(true);
    } else {
      // Standard flow - get auth link immediately
      mutations.getAuthLink({
        companyId: company.id,
        institutionId: institution.externalId,
        userName: authenticatedInfo?.name || '',
      });
    }

    return;
  };

  const onPsuIdSubmit = (psuId: string) => {
    setCollectedPsuId(psuId);
    setIsPsuIdModalOpen(false);

    // Now get auth link with PSU-ID
    if (selectedInstitution) {
      mutations.getAuthLink({
        companyId: company.id,
        institutionId: selectedInstitution.externalId,
        userName: authenticatedInfo?.name || '',
        psuId,
      });
    }
  };

  const closePsuIdModal = () => {
    setIsPsuIdModalOpen(false);
    setSelectedInstitution(null);
    setCollectedPsuId(undefined);
    localStorage.removeItem('selectedInstitution');
  };

  const closeModal = () => {
    setSelectedInstitution(null);
    setCollectedPsuId(undefined);

    localStorage.removeItem('selectedInstitution');
  };

  const onConnectAccounts = () => {
    if (data.authLinkData?.link) {
      window.open(data.authLinkData.link, '_blank', 'noopener,noreferrer');
    }
  };

  const filteredInstitutions: InstitutionView[] =
    data.institutionsData.institutions.filter((institution) =>
      institution.name.toLowerCase().includes(searchQuery.toLowerCase()),
    ) ?? [];

  const institutionData = localStorage.getItem('selectedInstitution');
  const cachedInstitution: InstitutionBasicInfo | null = institutionData ? JSON.parse(institutionData) : null;

  return (
    <div>
      {/* PSU-ID Collection Modal - shown first for Deutsche Bank/Noris Bank */}
      {selectedInstitution && isPsuIdModalOpen && (
        <PsuIdCollectionModal
          isOpen={isPsuIdModalOpen}
          onClose={closePsuIdModal}
          institutionName={selectedInstitution.name}
          institutionLogo={selectedInstitution.logo}
          onSubmit={onPsuIdSubmit}
        />
      )}

      {/* Connection Dialog - shown after PSU-ID is collected (if required) or immediately */}
      {selectedInstitution && companyCountry && !isPsuIdModalOpen && (
        <ConnectAccountDialog
          isOpen={!!selectedInstitution}
          closePopup={closeModal}
          institutionName={selectedInstitution.name}
          institutionLogo={selectedInstitution.logo}
          country={companyCountry}
          isInstitutionConnected={!!data.authLinkData?.link}
          isFetchingConnectionLink={isLoadingData.isFetchingAuthLink}
          connectionError={errorData.authLinkError}
          onConnectAccounts={onConnectAccounts}
          psuIdProvided={!!collectedPsuId}
        />
      )}

      <AccountConnectedSuccessModal
        isOpen={isAccountConnectModalOpen}
        onClose={() => navigate('/dashboard/transactions')}
        institutionName={cachedInstitution?.name}
        institutionLogo={cachedInstitution?.logo}
        accountsCount={data.syncStatusData?.syncStats.totalAccounts}
        onLinkAnotherAccount={() => setIsAccountConnectModalOpen(false)}
      />

      <AccountConnectedErrorModal
        isOpen={isAccountConnectErrorModalOpen}
        onOpenChange={() => setIsAccountConnectErrorModalOpen(false)}
      />

      <InstitutionList institutions={filteredInstitutions} onSelectInstitution={onInstitutionSelect} />
    </div>
  );
};

const getInstitutionDisplayData = (institution: InstitutionView): InstitutionBasicInfo => {
  return {
    name: institution.name,
    logo: institution.logo,
  };
};
